import { useState, useMemo } from "react";
import type { Field, Theme, FontPairing } from "@structurify/shared-types";
import { MonoLabel, QuietButton } from "../components/primitives/index.ts";
import { ExportTable } from "../export/ExportTable.tsx";

type Format = "table" | "csv" | "json";

interface Props {
  theme: Theme;
  fonts: FontPairing;
  fields: Field[];
  onSaveWorkflow: () => void;
  onDone: () => void;
}

export function StageExport({ theme, fonts, fields, onSaveWorkflow, onDone }: Props) {
  const [format, setFormat] = useState<Format>("table");
  const [copied, setCopied] = useState(false);

  const csvPreview = useMemo(() => {
    const head = fields.map((f) => f.label).join(",");
    const row = fields.map((f) => (/,/.test(f.value) ? `"${f.value}"` : f.value)).join(",");
    return `${head}\n${row}`;
  }, [fields]);

  const jsonPreview = useMemo(() => {
    const obj = Object.fromEntries(fields.map((f) => [f.id, f.value]));
    return JSON.stringify(obj, null, 2);
  }, [fields]);

  const text = format === "csv" ? csvPreview : format === "json" ? jsonPreview : "";

  const handleCopy = () => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "clamp(20px, 4vw, 48px)", overflow: "auto" }}>
      <div style={{ width: "100%", maxWidth: 980, opacity: 0, animation: "fadeUp 600ms forwards ease-out" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 18, marginBottom: 10 }}>
          <MonoLabel theme={theme} fonts={fonts} size={10}>READY</MonoLabel>
          <div style={{ flex: 1, height: 1, background: theme.rule }} />
          <MonoLabel theme={theme} fonts={fonts} size={10} color={theme.inkMute}>14 / 14 VERIFIED</MonoLabel>
        </div>

        <h2 style={{ fontFamily: fonts.headerFamily === "serif" ? fonts.serif : fonts.sans, fontWeight: fonts.headerWeight, fontSize: 38, lineHeight: 1.1, letterSpacing: fonts.headerTracking, color: theme.ink, margin: "12px 0 14px 0" }}>
          Clean structured data, with proof.
        </h2>
        <p style={{ fontFamily: fonts.sans, fontSize: 15, color: theme.inkSoft, lineHeight: 1.55, margin: 0, marginBottom: 28, maxWidth: 580 }}>
          Every value below traces back to its location in the original document. Download, copy, or send it onward.
        </p>

        <div style={{ display: "flex", alignItems: "center", gap: 0, marginBottom: 14, borderBottom: `1px solid ${theme.rule}` }}>
          {(["table", "csv", "json"] as Format[]).map((f) => (
            <button
              key={f}
              onClick={() => setFormat(f)}
              style={{ fontFamily: fonts.mono, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", padding: "10px 18px", background: "transparent", border: "none", color: format === f ? theme.ink : theme.inkMute, borderBottom: `1px solid ${format === f ? theme.ink : "transparent"}`, marginBottom: -1, cursor: "pointer", fontWeight: format === f ? 600 : 500 }}
            >
              {f}
            </button>
          ))}
          <div style={{ flex: 1 }} />
          {format !== "table" && (
            <button onClick={handleCopy} style={{ fontFamily: fonts.sans, fontSize: 12, color: theme.inkSoft, background: "transparent", border: "none", cursor: "pointer", padding: "8px 0" }}>
              {copied ? "✓ Copied" : "Copy to clipboard"}
            </button>
          )}
        </div>

        {format === "table" ? (
          <ExportTable fields={fields} theme={theme} fonts={fonts} />
        ) : (
          <div style={{ background: theme.docPaper, border: `1px solid ${theme.rule}`, padding: "20px 22px", maxHeight: 360, overflow: "auto", fontFamily: fonts.mono, fontSize: 12, lineHeight: 1.7, color: theme.ink, whiteSpace: "pre" }}>
            {text}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 28, gap: 14, flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: 10 }}>
            <QuietButton theme={theme} fonts={fonts} primary onClick={onDone}>
              Download {format === "table" ? "CSV" : format.toUpperCase()}
              <svg width="11" height="11" viewBox="0 0 11 11"><path d="M5.5 1.5 L5.5 7.5 M2.5 5 L5.5 8 L8.5 5 M2 9.5 L9 9.5" stroke="currentColor" strokeWidth="1" fill="none" /></svg>
            </QuietButton>
            <QuietButton theme={theme} fonts={fonts}>Send to integration</QuietButton>
          </div>
          <button
            onClick={onSaveWorkflow}
            style={{ fontFamily: fonts.sans, fontSize: 13, color: theme.ink, background: "transparent", border: `1px dashed ${theme.ruleStrong}`, padding: "10px 16px", cursor: "pointer", borderRadius: 2, display: "inline-flex", alignItems: "center", gap: 8 }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12">
              <rect x="1.5" y="1.5" width="9" height="9" fill="none" stroke={theme.ink} strokeWidth="1" />
              <line x1="6" y1="3.5" x2="6" y2="8.5" stroke={theme.ink} />
              <line x1="3.5" y1="6" x2="8.5" y2="6" stroke={theme.ink} />
            </svg>
            Save this as a reusable workflow
          </button>
        </div>
      </div>
    </div>
  );
}
